/**
 * Фильтры форматов для `<input accept>`.
 *
 * Держим синхронно с тем, что реально принимает doc-processor
 * (`doc-processor/app/main.py`, детект по расширению + magic-сигнатуре).
 * Рассинхрон стоит дорого в обе стороны: слишком узкий accept прячет файл,
 * который бэкенд разобрал бы (юрист руками переключает диалог на «Все файлы»),
 * слишком широкий — даёт 400 уже после долгой заливки комплекта.
 */

export const ACCEPT_PDF = '.pdf';
export const ACCEPT_WORD = '.docx,.doc';
/** `.xls` doc-processor конвертит в `.xlsx` сам (LibreOffice), `.xlsb` — нет. */
export const ACCEPT_EXCEL = '.xlsx,.xlsm,.xls';
export const ACCEPT_TEXT = '.txt,.md,.markdown';
export const ACCEPT_ARCHIVE = '.zip,.rar';
/** Сканы и фото с телефона — doc-processor прогоняет их через OCR. */
export const ACCEPT_IMAGE = '.jpg,.jpeg,.jpe,.png,.tif,.tiff,.webp,.bmp,.gif,.heic,.heif';

/** Без фильтра: атрибут не рендерится, диалог открывается на «Все файлы». */
export const ACCEPT_ANY = '';

const join = (...parts: string[]): string => parts.join(',');

/** Договор с приложениями и спецификациями: комплект целиком, включая архив папки. */
export const ACCEPT_CONTRACT_SET = join(
  ACCEPT_PDF,
  ACCEPT_WORD,
  ACCEPT_EXCEL,
  ACCEPT_ARCHIVE,
  ACCEPT_IMAGE,
);

/** Претензии, уставы, доверенности — документ или его скан, архивы не нужны. */
export const ACCEPT_LEGAL_DOC = join(ACCEPT_PDF, ACCEPT_WORD, ACCEPT_IMAGE);

/** Расчёты и переписка по делу: то же плюс Excel (расчёт неустойки обычно в нём). */
export const ACCEPT_CLAIM_DOC = join(ACCEPT_PDF, ACCEPT_WORD, ACCEPT_EXCEL, ACCEPT_IMAGE);

/** База знаний: текстовые документы, сканы сюда не грузят. */
export const ACCEPT_KB_DOC = join(ACCEPT_PDF, ACCEPT_WORD, ACCEPT_TEXT);

/** Корпоративный шаблон договора — только редактируемый документ. */
export const ACCEPT_TEMPLATE = join(ACCEPT_PDF, ACCEPT_WORD);

/** `/docx/structure` разбирает только DOCX; legacy `.doc` конвертится на бэке сам. */
export const ACCEPT_DOCX_ONLY = ACCEPT_WORD;

/** Документ на комплаенс-проверку: договор или его скан. */
export const ACCEPT_COMPLIANCE_DOC = join(ACCEPT_PDF, ACCEPT_WORD, ACCEPT_TEXT, ACCEPT_IMAGE);

const DESCRIPTIONS: ReadonlyArray<readonly [string, string]> = [
  [ACCEPT_PDF, 'PDF'],
  [ACCEPT_WORD, 'DOCX'],
  [ACCEPT_EXCEL, 'XLSX'],
  [ACCEPT_TEXT, 'TXT'],
  [ACCEPT_ARCHIVE, 'ZIP'],
  [ACCEPT_IMAGE, 'сканы'],
];

/**
 * Человекочитаемое перечисление форматов для подписи дропзоны — чтобы подпись
 * не разъезжалась с фильтром, как это было, пока обе строки писали руками.
 */
export function describeAccept(accept: string): string {
  if (!accept) return 'файлы любого формата';
  const exts = new Set(accept.split(',').map((e) => e.trim().toLowerCase()));
  const shown = DESCRIPTIONS.filter(([group]) =>
    group.split(',').some((ext) => exts.has(ext)),
  ).map(([, label]) => label);
  return shown.length > 0 ? shown.join(' / ') : 'файлы';
}
